import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CheckCircle, AlertTriangle, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Displays preview of reference lists import operations with validation results
 * @param {Object} props
 * @param {Object} props.preview - Preview data with lists, values, synonyms
 * @param {Array} props.errors - Array of error objects
 * @param {Array} props.warnings - Array of warning objects
 */
export function ReferenceListsImportPreview({ preview, errors = [], warnings = [] }) {
  const [expandedUpdates, setExpandedUpdates] = useState({});

  if (!preview && errors.length === 0 && warnings.length === 0) {
    return null;
  }

  const {
    lists = { toAdd: [], toUpdate: [] },
    values = { toAdd: [], toUpdate: [] },
    synonyms = { toAdd: [], toUpdate: [] },
    summary = {}
  } = preview || {};

  const totalLists = (lists.toAdd?.length || 0) + (lists.toUpdate?.length || 0);
  const totalValues = (values.toAdd?.length || 0) + (values.toUpdate?.length || 0);
  const totalSynonyms = (synonyms.toAdd?.length || 0) + (synonyms.toUpdate?.length || 0);
  const hasErrors = errors.length > 0;
  const hasWarnings = warnings.length > 0;

  const toggleUpdate = (key) => {
    setExpandedUpdates(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Active': 'bg-green-100 text-green-800',
      'Under Review': 'bg-yellow-100 text-yellow-800',
      'Deprecated': 'bg-gray-100 text-gray-800'
    };
    return (
      <Badge className={colors[status] || 'bg-gray-100 text-gray-800'}>
        {status || 'Active'}
      </Badge>
    );
  };

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Lists
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalLists}</div>
            <div className="text-xs text-muted-foreground">
              {lists.toAdd?.length || 0} new, {lists.toUpdate?.length || 0} update
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Values
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalValues}</div>
            <div className="text-xs text-muted-foreground">
              {values.toAdd?.length || 0} new, {values.toUpdate?.length || 0} update
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Synonyms
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalSynonyms}</div>
            <div className="text-xs text-muted-foreground">
              {synonyms.toAdd?.length || 0} new
            </div>
          </CardContent>
        </Card>

        <Card className="border-green-200 bg-green-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-green-700">
              New Items
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">
              {(lists.toAdd?.length || 0) + (values.toAdd?.length || 0) + (synonyms.toAdd?.length || 0)}
            </div>
          </CardContent>
        </Card>

        <Card className={hasErrors ? "border-red-200 bg-red-50" : ""}>
          <CardHeader className="pb-3">
            <CardTitle className={`text-sm font-medium ${hasErrors ? 'text-red-700' : 'text-muted-foreground'}`}>
              Errors
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${hasErrors ? 'text-red-700' : ''}`}>
              {errors.length}
            </div>
          </CardContent>
        </Card>

        <Card className={hasWarnings ? "border-yellow-200 bg-yellow-50" : ""}>
          <CardHeader className="pb-3">
            <CardTitle className={`text-sm font-medium ${hasWarnings ? 'text-yellow-700' : 'text-muted-foreground'}`}>
              Warnings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${hasWarnings ? 'text-yellow-700' : ''}`}>
              {warnings.length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Errors Alert */}
      {hasErrors && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {errors.length} validation error{errors.length !== 1 ? 's' : ''} found. Please fix these issues before importing.
          </AlertDescription>
        </Alert>
      )}

      {/* Warnings Alert */}
      {hasWarnings && (
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800">
            {warnings.length} warning{warnings.length !== 1 ? 's' : ''} found. These won't prevent import but should be reviewed.
          </AlertDescription>
        </Alert>
      )}

      {/* Tabs for Different Sections */}
      <Tabs defaultValue={hasErrors ? "errors" : "lists"} className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="lists" className="relative">
            New Lists
            {lists.toAdd?.length > 0 && (
              <Badge variant="secondary" className="ml-2">{lists.toAdd.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="list-updates" className="relative">
            List Updates
            {lists.toUpdate?.length > 0 && (
              <Badge variant="secondary" className="ml-2">{lists.toUpdate.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="values" className="relative">
            New Values
            {values.toAdd?.length > 0 && (
              <Badge variant="secondary" className="ml-2">{values.toAdd.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="synonyms" className="relative">
            Synonyms
            {synonyms.toAdd?.length > 0 && (
              <Badge variant="secondary" className="ml-2">{synonyms.toAdd.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="errors" className="relative">
            Errors
            {hasErrors && (
              <Badge variant="destructive" className="ml-2">{errors.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="warnings" className="relative">
            Warnings
            {hasWarnings && (
              <Badge variant="secondary" className="ml-2 bg-yellow-100 text-yellow-800">{warnings.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* New Lists Tab */}
        <TabsContent value="lists" className="space-y-4">
          {(lists.toAdd?.length || 0) === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center text-muted-foreground">
                  <Info className="h-8 w-8 mx-auto mb-2" />
                  <p>No new reference lists to add</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>List Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lists.toAdd.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{item.list_name}</TableCell>
                        <TableCell className="max-w-md truncate">{item.description || '-'}</TableCell>
                        <TableCell>{item.category || '-'}</TableCell>
                        <TableCell>{getStatusBadge(item.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* List Updates Tab */}
        <TabsContent value="list-updates" className="space-y-4">
          {(lists.toUpdate?.length || 0) === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center text-muted-foreground">
                  <Info className="h-8 w-8 mx-auto mb-2" />
                  <p>No existing lists to update</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {lists.toUpdate.map((item, index) => (
                <Card key={index} className="border-blue-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-blue-600" />
                        <CardTitle className="text-base">{item.list_name}</CardTitle>
                        {getStatusBadge(item.status)}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleUpdate(`list-${index}`)}
                      >
                        {expandedUpdates[`list-${index}`] ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </CardHeader>
                  {expandedUpdates[`list-${index}`] && (
                    <CardContent className="space-y-3">
                      {item.changes && item.changes.map((change, changeIndex) => (
                        <div key={changeIndex} className="space-y-1">
                          <div className="text-sm font-medium text-muted-foreground">
                            {change.field}:
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="p-2 bg-red-50 border border-red-200 rounded">
                              <div className="text-xs text-red-600 font-medium mb-1">Old:</div>
                              <div className="text-red-900">{change.oldValue || '(empty)'}</div>
                            </div>
                            <div className="p-2 bg-green-50 border border-green-200 rounded">
                              <div className="text-xs text-green-600 font-medium mb-1">New:</div>
                              <div className="text-green-900">{change.newValue || '(empty)'}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                      {!item.changes && (
                        <div className="text-sm text-muted-foreground">
                          No changes detected
                        </div>
                      )}
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* New Values Tab */}
        <TabsContent value="values" className="space-y-4">
          {(values.toAdd?.length || 0) === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center text-muted-foreground">
                  <Info className="h-8 w-8 mx-auto mb-2" />
                  <p>No new values to add</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>List Name</TableHead>
                      <TableHead>Canonical Value</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Active</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {values.toAdd.slice(0, 100).map((item, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{item.list_name}</TableCell>
                        <TableCell>{item.canonical_value}</TableCell>
                        <TableCell className="max-w-md truncate">{item.description || '-'}</TableCell>
                        <TableCell>
                          <Badge variant={item.is_active !== false ? 'default' : 'secondary'}>
                            {item.is_active !== false ? 'Yes' : 'No'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {values.toAdd.length > 100 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground">
                          ... and {values.toAdd.length - 100} more values
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Synonyms Tab */}
        <TabsContent value="synonyms" className="space-y-4">
          {(synonyms.toAdd?.length || 0) === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center text-muted-foreground">
                  <Info className="h-8 w-8 mx-auto mb-2" />
                  <p>No new synonyms to add</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>List Name</TableHead>
                      <TableHead>Canonical Value</TableHead>
                      <TableHead>Synonym</TableHead>
                      <TableHead>Approved</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {synonyms.toAdd.slice(0, 100).map((item, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{item.list_name}</TableCell>
                        <TableCell>{item.canonical_value}</TableCell>
                        <TableCell>{item.synonym}</TableCell>
                        <TableCell>
                          <Badge variant={item.is_approved !== false ? 'default' : 'secondary'}>
                            {item.is_approved !== false ? 'Yes' : 'No'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {synonyms.toAdd.length > 100 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground">
                          ... and {synonyms.toAdd.length - 100} more synonyms
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Errors Tab */}
        <TabsContent value="errors" className="space-y-4">
          {errors.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center text-green-600">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2" />
                  <p>No validation errors found</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Sheet</TableHead>
                      <TableHead>Row</TableHead>
                      <TableHead>Column</TableHead>
                      <TableHead>Error</TableHead>
                      <TableHead>Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {errors.map((error, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">
                          {error.sheet || '-'}
                        </TableCell>
                        <TableCell>
                          {error.row || '-'}
                        </TableCell>
                        <TableCell>{error.column || '-'}</TableCell>
                        <TableCell className="text-red-600">{error.message}</TableCell>
                        <TableCell className="max-w-xs truncate font-mono text-xs">
                          {error.value !== undefined ? String(error.value).substring(0, 50) : '-'}
                          {error.value && String(error.value).length > 50 ? '...' : ''}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Warnings Tab */}
        <TabsContent value="warnings" className="space-y-4">
          {warnings.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center text-green-600">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2" />
                  <p>No warnings</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Sheet</TableHead>
                      <TableHead>Row</TableHead>
                      <TableHead>Column</TableHead>
                      <TableHead>Warning</TableHead>
                      <TableHead>Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {warnings.map((warning, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">
                          {warning.sheet || '-'}
                        </TableCell>
                        <TableCell>
                          {warning.row || '-'}
                        </TableCell>
                        <TableCell>{warning.column || '-'}</TableCell>
                        <TableCell className="text-yellow-700">{warning.message}</TableCell>
                        <TableCell className="max-w-xs truncate font-mono text-xs">
                          {warning.value !== undefined ? String(warning.value).substring(0, 50) : '-'}
                          {warning.value && String(warning.value).length > 50 ? '...' : ''}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
